/**
 * OrderProgressBar — segmented phase indicator shared by the B2C tracking view
 * (`/track/:token`) and the B2B FleetHub list. Renders 7 segments for
 * pickup_delivery orders (2 for driveby): completed phases are medium grey,
 * upcoming phases are light grey, and the *current* phase is brand red.
 *
 * The active (red) segment is alive: a high-contrast light streak sweeps
 * left→right on a seamless infinite loop, signalling "this phase is happening
 * right now" with motion rather than a flat fill. The streak's gradient edges
 * are transparent, so the loop wrap is imperceptible and the motion reads as
 * one continuous flow.
 *
 * Exception: the *final* stage is not animated. Reaching the last segment means
 * the order is already finished, so that segment stays a static red fill —
 * nothing is in progress to sweep.
 *
 * @module components/OrderProgressBar
 */

import { FC, useEffect, useId, useState } from "react"
import { LayoutChangeEvent, View, ViewStyle } from "react-native"
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated"
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg"

import { colors, spacing } from "@/theme"

/** Bar/segment height in px — single source of truth (layout + the SVG streak). */
const BAR_HEIGHT = 10
/** Duration of one left→right pass of the light streak. */
const SWEEP_DURATION_MS = 1600
/** Streak width as a fraction of the segment width, clamped to a sensible min. */
const SHIMMER_RATIO = 0.62
const SHIMMER_MIN_WIDTH = 26

interface OrderProgressBarProps {
  currentPhase: string | number
  style?: ViewStyle
  isDriveby?: boolean
}

export const OrderProgressBar: FC<OrderProgressBarProps> = ({ currentPhase, style, isDriveby }) => {
  // Convert phase to number if it's a string
  const currentPhaseNumber = typeof currentPhase === "number" ? currentPhase : 1 // Default to 1 if string status provided

  // For driveby, we only have 2 phases
  const totalPhases = isDriveby ? 2 : 7

  return (
    <View style={[$container, style]}>
      {/* Phase segments - 2 segments for driveby, 7 for pickup_delivery */}
      <View style={$segmentsContainer}>
        {Array.from({ length: totalPhases }).map((_, index) => {
          const phaseNumber = index + 1
          const isCompleted = phaseNumber < currentPhaseNumber
          const isActive = phaseNumber === currentPhaseNumber
          // The last stage means the order is finished — render a static red
          // fill there, never the "in-progress" sweep.
          const isLastStage = phaseNumber === totalPhases

          return (
            <View key={index} style={$segmentWrapper}>
              {isActive && !isLastStage ? (
                <ActivePhaseSegment />
              ) : (
                <View
                  style={[$segment, isCompleted && $segmentCompleted, isActive && $segmentActive]}
                />
              )}
            </View>
          )
        })}
      </View>
    </View>
  )
}

/**
 * The current-phase segment: brand-red fill with a light streak that sweeps
 * across it forever. Segments are flex-sized, so the width is measured at
 * runtime — the streak then scales to (and travels) the segment's full span.
 */
const ActivePhaseSegment: FC = () => {
  // Unique gradient id per instance — the FleetHub list renders many bars, and
  // duplicate SVG gradient ids collide in the DOM on web. Strip colons from
  // useId() so the value is safe inside `url(#...)`.
  const gradientId = `phaseShimmer-${useId().replace(/:/g, "")}`
  const [width, setWidth] = useState(0)
  const translateX = useSharedValue(0)

  const shimmerWidth = Math.max(SHIMMER_MIN_WIDTH, width * SHIMMER_RATIO)

  useEffect(() => {
    if (width <= 0) return
    // Park the streak just off the left edge, then glide its right edge past
    // the segment's right edge and loop. Both ends of the streak are fully
    // transparent, so the wrap from right→left is invisible (no "snap").
    translateX.value = -shimmerWidth
    translateX.value = withRepeat(
      withTiming(width, { duration: SWEEP_DURATION_MS, easing: Easing.inOut(Easing.ease) }),
      -1, // infinite
      false, // restart each loop (left→right always), never reverse
    )
    return () => cancelAnimation(translateX)
  }, [width, shimmerWidth, translateX])

  const shimmerStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }))

  const onLayout = (e: LayoutChangeEvent) => {
    const w = Math.round(e.nativeEvent.layout.width)
    if (w && w !== width) setWidth(w)
  }

  return (
    <View style={[$segment, $segmentActive]} onLayout={onLayout}>
      {width > 0 && (
        <Animated.View
          style={[$shimmer, { width: shimmerWidth }, shimmerStyle]}
          pointerEvents="none"
        >
          <Svg width={shimmerWidth} height={BAR_HEIGHT}>
            <Defs>
              {/* Transparent → tight bright core → transparent = a soft, premium
                  specular streak rather than a hard skeleton-loader bar. */}
              <LinearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                <Stop offset="0" stopColor="#FFFFFF" stopOpacity={0} />
                <Stop offset="0.45" stopColor="#FFFFFF" stopOpacity={0.6} />
                <Stop offset="0.5" stopColor="#FFFFFF" stopOpacity={0.95} />
                <Stop offset="0.55" stopColor="#FFFFFF" stopOpacity={0.6} />
                <Stop offset="1" stopColor="#FFFFFF" stopOpacity={0} />
              </LinearGradient>
            </Defs>
            <Rect
              x="0"
              y="0"
              width={shimmerWidth}
              height={BAR_HEIGHT}
              fill={`url(#${gradientId})`}
            />
          </Svg>
        </Animated.View>
      )}
    </View>
  )
}

const $container: ViewStyle = {
  height: BAR_HEIGHT,
  position: "relative",
  marginVertical: spacing.md,
}

const $segmentsContainer: ViewStyle = {
  flexDirection: "row",
  height: "100%",
  justifyContent: "space-between",
  gap: 6, // Increased gap (was 4)
}

const $segmentWrapper: ViewStyle = {
  flex: 1,
  marginHorizontal: 1,
  paddingHorizontal: 1, // Changed from 0.5 to avoid React Native Web text node errors
}

const $segment: ViewStyle = {
  height: "100%",
  backgroundColor: colors.progressBarIncomplete, // Lighter grey for incomplete
  borderRadius: 5, // More rounded
}

const $segmentCompleted: ViewStyle = {
  backgroundColor: colors.progressBarComplete, // Medium grey for completed
  // Subtle inner shadow effect (simulated with border)
  borderWidth: 0.5,
  borderColor: "rgba(0, 0, 0, 0.1)",
}

const $segmentActive: ViewStyle = {
  backgroundColor: colors.primary, // HolDeinePlakette red
  overflow: "hidden", // clip the travelling streak to the rounded segment
  // Enhanced with subtle glow effect (box-shadow is painted outside the box,
  // so overflow:hidden does not clip it on web).
  shadowColor: colors.primary,
  shadowOffset: { width: 0, height: 0 },
  shadowOpacity: 0.4,
  shadowRadius: 6,
  elevation: 3,
}

const $shimmer: ViewStyle = {
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
}
